import React from "react";
import ReactDOM from "react-dom";

export interface IToast {
  message: string;
  type?: "error" | "default";
  duration?: number;
}

let _notificationAPI: NotificationAPI | undefined;

export interface INoticeItem {
  toastItem: IToast;
  onDisapear: () => void;
}

export class Notice extends React.Component<Omit<IToast, "duration">, {}> {
  static DEFAULT_ENTER_DURATION = 100;
  static DEFAULT_LEAVE_DURATION = 100;
  render() {
    return (
      <div
        className={
          "icode-component-notice" + (this.props.type ? ` ${this.props.type}` : "")
        }
      >
        <div className="icode-component-notice-content">{this.props.message}</div>
      </div>
    );
  }
  performNotiveEnter = (): Promise<void> => {
    return Promise.resolve();
  };
  performNoticeLeave = (): Promise<void> => {
    return Promise.resolve();
  };
  setNoticeTop = (top: number) => {
    const dom = ReactDOM.findDOMNode(this) as HTMLDivElement;
    if (dom) {
      dom.style.top = `${top}px`;
      top += dom.offsetHeight;
    }
    return top;
  };
}

export interface INotificationState {
  notices: {
    [k: string]: INoticeItem;
  };
}
export class Notification extends React.Component<{}, INotificationState> {
  static DEFAULT_DURATION = 3000;
  static DEFAULT_MARGIN = 10;
  state: INotificationState = {
    notices: {}
  };
  childComponents: { [key: string]: Notice } = {};
  getNoticeLength = () => {
    return Object.keys(this.state.notices).length;
  };
  addNotice = (key: string, notice: IToast) => {
    return new Promise<void>((resolve) => {
      this.setState((state) => {
        return {
          notices: {
            ...state.notices,
            [key]: {
              toastItem: notice,
              onDisapear: resolve
            }
          }
        };
      });
    });
  };

  performNoticeLeave = (key: string) => {
    const component = this.childComponents[key];
    if (component) {
      return component.performNoticeLeave();
    }
    return Promise.resolve();
  };

  removeNoticeFromState = (key: string) => {
    this.setState((state) => {
      const notices: { [k: string]: INoticeItem } = {};
      for (const innterKey in state.notices) {
        if (key !== innterKey) {
          notices[innterKey] = state.notices[innterKey];
        }
      }
      return {
        notices
      };
    });
  };

  removeNotice = (key: string) => {
    this.performNoticeLeave(key).then(() => {
      this.removeNoticeFromState(key);
    });
  };
  componentDidUpdate(prevProps: any, prevState: INotificationState) {
    const state = this.state;
    const { notices } = state;
    let sum = Notification.DEFAULT_MARGIN;
    for (const key in notices) {
      const component = this.childComponents[key];
      const nextSum = component.setNoticeTop(sum);
      if (nextSum !== sum) {
        sum = nextSum + Notification.DEFAULT_MARGIN;
      }
      if (!(key in prevState.notices)) {
        component.performNotiveEnter();
      }
    }

    for (const key2 in prevState.notices) {
      if (!(key2 in notices)) {
        prevState.notices[key2].onDisapear();
      }
    }
  }

  render() {
    const noticeElements = [];
    for (const key in this.state.notices) {
      const item = this.state.notices[key];
      const ref = (c: Notice) => {
        this.childComponents[key] = c;
      };
      noticeElements.push(
        <Notice
          key={key}
          type={item.toastItem.type}
          message={item.toastItem.message}
          ref={ref}
        />
      );
    }
    return <div className="icode-component-notification">{noticeElements}</div>;
  }
}

export interface NotificationAPI {
  component: Notification;
  addNotice: Notification["addNotice"];
  removeNotice: Notification["removeNotice"];
  destroy(): void;
}

export function getNotificationInstance(): Promise<NotificationAPI> {
  return new Promise((resolve) => {
    if (_notificationAPI) {
      resolve(_notificationAPI);
    } else {
      const container = document.createElement("div");
      document.body.appendChild(container);
      const ref = (inst: Notification) => {
        if (inst) {
          const api = {
            component: inst,
            addNotice: inst.addNotice,
            removeNotice: inst.removeNotice,
            destroy() {
              if (inst.getNoticeLength()) {
                return;
              }
              _notificationAPI = undefined;
              ReactDOM.unmountComponentAtNode(container);
              if (container.parentNode) {
                container.parentNode.removeChild(container);
              }
            }
          };
          _notificationAPI = api;
          resolve(api);
        }
      };
      ReactDOM.render(<Notification ref={ref} />, container);
    }
  });
}

export function showToast(options: IToast) {
  getNotificationInstance().then((notification) => {
    const duration = options.duration || 3000;
    const noticeId = Math.random().toString(32).substr(2);
    const timmer = setTimeout(() => {
      notification.removeNotice(noticeId);
    }, duration);
    notification.addNotice(noticeId, options).then(() => {
      clearTimeout(timmer);
      notification.destroy();
    });
    return () => {
      notification.removeNotice(noticeId);
    };
  });
}
